import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileDown } from "lucide-react";
import { MaskedLines, Reveal, SectionHeader } from "@/components/motion";
import { usePageMeta } from "@/lib/seo";

const CORE_CAPABILITIES = [
  "Business consulting and management",
  "Leadership and professional development",
  "Marketing and communications",
  "Administrative support",
  "Business operations support",
  "Procurement and supply",
  "Logistics and delivery",
  "Other professional and support services as applicable",
];

const SUPPLY_AREAS = [
  {
    title: "Supply & Sourcing",
    text: "Product and material supply, sourcing support, and purchasing solutions aligned to defined requirements.",
  },
  {
    title: "Fulfillment & Delivery",
    text: "Fulfillment coordination, delivery support, and logistics services that keep operations moving.",
  },
  {
    title: "Operational Support",
    text: "Administrative, documentation, and business operations support services for program execution.",
  },
];

export default function GovernmentContracting() {
  usePageMeta(
    "Government Contracting & Procurement — Iconicho, LLC",
    "Iconicho, LLC is a diversified business solutions provider supporting government contracting and procurement needs across professional services, administrative support, training, marketing, supplies, and logistics."
  );
  return (
    <>
      <section className="noise-overlay bg-navy pt-36 lg:pt-44" data-testid="gov-hero">
        <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs font-medium uppercase tracking-[0.3em] text-white/60">
                Public Sector
              </span>
            </div>
          </Reveal>
          <MaskedLines
            testId="gov-headline"
            className="mt-8 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            delay={0.15}
            lines={[
              "Government Contracting",
              <span key="l2" className="text-gold">&amp; Procurement</span>,
            ]}
          />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/70">
              Iconicho, LLC is a diversified business solutions provider
              positioned to support government and commercial procurement needs —
              from professional services and administrative support to supplies,
              logistics, and delivery.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="gov-core-capabilities">
        <SectionHeader
          index="01"
          overline="Contract Capabilities"
          title="Core Service Capabilities"
        />
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl leading-relaxed text-charcoal/70">
            Iconicho is structured to respond to a broad range of organizational
            and procurement requirements, with capabilities that can be tailored
            to each contract scope.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-charcoal/10 bg-charcoal/10 sm:grid-cols-2">
          {CORE_CAPABILITIES.map((cap, i) => (
            <Reveal key={cap} delay={0.04 * i} className="h-full">
              <div className="flex h-full items-center gap-5 bg-white px-8 py-6">
                <span className="font-heading text-xs font-bold tracking-[0.25em] text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm font-medium text-navy">{cap}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-charcoal/10" data-testid="gov-supply-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeader
                index="02"
                overline="Supply & Procurement"
                title="Beyond Professional Services"
              />
              <Reveal delay={0.1}>
                <p className="mt-6 leading-relaxed text-charcoal/70">
                  Iconicho is structured to pursue opportunities that extend
                  beyond traditional consulting and professional services —
                  including applicable supply, procurement, fulfillment,
                  delivery, and operational support requirements.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="flex h-full flex-col justify-center gap-8">
                {SUPPLY_AREAS.map((area, i) => (
                  <Reveal key={area.title} delay={0.08 * i}>
                    <div className="border-l-2 border-gold/50 pl-6">
                      <h3 className="font-heading text-lg font-bold tracking-tight text-navy">
                        {area.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-charcoal/65">
                        {area.text}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="noise-overlay bg-navy" data-testid="gov-partnerships-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <SectionHeader
                dark
                index="03"
                overline="Contracting Partnerships"
                title="Built for Collaboration"
              />
              <Reveal delay={0.12}>
                <p className="mt-8 max-w-xl leading-relaxed text-white/70">
                  Iconicho welcomes appropriate contracting, subcontracting,
                  vendor, supplier, and strategic partnership opportunities.
                  Prime contractors and procurement professionals are invited to
                  connect directly with our team.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.2} className="lg:text-right">
                <Link
                  to="/contact"
                  data-testid="gov-partnerships-cta-button"
                  className="group inline-flex items-center gap-3 rounded-sm bg-gold px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-gold-dark"
                >
                  Contact Iconicho
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="capability-statement-section">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <SectionHeader
              index="04"
              overline="Procurement Resources"
              title="Capability Statement"
            />
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl leading-relaxed text-charcoal/70">
                Our capability statement is being finalized and will be
                available here for download. For immediate capability
                discussions, contact our team directly.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <div className="rounded-sm border border-dashed border-charcoal/25 bg-white p-10 text-center">
                <FileDown className="mx-auto h-8 w-8 text-gold" strokeWidth={1.5} />
                <p className="mt-4 text-sm text-charcoal/60">
                  Capability Statement (PDF)
                </p>
                <button
                  type="button"
                  disabled
                  data-testid="capability-statement-button"
                  className="mt-6 w-full cursor-not-allowed rounded-sm border border-charcoal/20 px-6 py-3.5 text-sm font-medium tracking-wide text-charcoal/50"
                >
                  Capability Statement — Coming Soon
                </button>
                <Link
                  to="/contact"
                  data-testid="capability-statement-contact-link"
                  className="group mt-5 inline-flex items-center gap-2 text-sm font-medium text-navy"
                >
                  <span className="border-b border-gold/50 pb-0.5 transition-colors duration-300 group-hover:border-gold">
                    Request details by email
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-gold" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
